import type { ReactElement } from 'react'
import { fogChess } from '@games/fog-chess'
import { ChatDock } from '../components/ChatDock'
import { ConnectionBadge } from '../components/ConnectionBadge'
import { RoomCodeBox } from '../components/RoomCodeBox'
import { getGame } from '../registry'
import { useChat } from '../stores/chat'
import type { RoomSession } from './useRoomSession'

export interface RoomScreenProps {
  readonly session: RoomSession
}

/**
 * ⚠️ 既有欠债：房间层早就硬编码了 fog-chess 的座位（`'r' | 'b'`，见 `roomReducer.ts`），
 * 彻底解耦需要 `packages/core` 契约变更（例如 `module.labelFor(seat)`），属另一项任务。
 */
const seatLabel = (seat: string): string => (seat === 'b' ? '黑方' : '红方')

/**
 * 对局整屏：顶栏（连接态 + 房主等待期的房间号）→ 棋盘区（flex-1 min-h-0 才能拿到真实高度）
 * → 结算/断线提示。整屏不滚动。
 *
 * 布局随断点切换（聊天由 `ChatDock` 摆放，**不占对流空间**）：
 * - `≥ md`：左右两栏，左游戏右聊天室；
 * - `< md`：单栏，聊天收成右下角悬浮按钮，点开为全屏覆盖层。
 */
export function RoomScreen({ session }: RoomScreenProps): ReactElement {
  const { state } = session
  const messages = useChat((store) => store.messages)
  const unread = useChat((store) => store.unread)

  const entry = getGame('fog-chess')
  const game = state.game
  const self = state.self
  const isSpectator = state.role === 'spectator'
  const view =
    game === null
      ? null
      : isSpectator || self === null
        ? fogChess.viewFor(game, { kind: 'spectator' })
        : fogChess.viewFor(game, { kind: 'player', playerId: self })
  const outcome = game ? fogChess.outcome(game) : null
  const winner = game ? (game.winner ?? outcome?.winner ?? null) : null
  const finished = winner !== null || outcome !== null
  const disconnected = game !== null && state.status === 'closed'
  const showResult = finished || disconnected
  // 观众没有座位：永远不能走子
  const disabled =
    game === null ||
    self === null ||
    isSpectator ||
    game.turn !== self ||
    finished ||
    state.status !== 'connected'

  /** 观众人数由房主广播，所有人都能看到（D12） */
  const watchingCount = state.audienceCount

  const resultText = disconnected
    ? (state.error ?? '对方已断开连接。')
    : winner === null
      ? '平局'
      : isSpectator
        ? `${seatLabel(winner)} 获胜`
        : winner === self
          ? '你赢了！'
          : '你输了'

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-2 sm:p-3 md:flex-row">
      {/* 左列：对局本体。右列（≥md）由 ChatDock 提供 */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        <header className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <ConnectionBadge status={state.status} error={state.error} />
          {isSpectator ? (
            <span className="rounded-lg border border-warning/50 bg-warning/10 px-2.5 py-1 text-xs text-warning">
              观战中（不能走子）
            </span>
          ) : null}
          {watchingCount > 0 ? (
            <span className="text-xs text-white/60">{watchingCount} 人观战中</span>
          ) : null}
          <button
            type="button"
            className="rounded-lg border border-white/25 px-3 py-1.5 text-sm text-white/90 hover:bg-white/10"
            onClick={() => session.leave()}
          >
            {isSpectator ? '退出观战' : '离开对局'}
          </button>
        </header>

        {state.phase === 'hosting' && state.roomCode && !game ? (
          <div className="shrink-0 rounded-[10px] bg-black/20 px-3 pb-2">
            <RoomCodeBox roomCode={state.roomCode} shareLink={state.shareLink ?? ''} />
          </div>
        ) : null}

        {/* 移动端右下角有悬浮聊天按钮，底部给它留位，避免盖住棋盘角上的格子 */}
        <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center rounded-[10px] bg-black/20 pb-20 md:pb-0">
          {entry && view ? (
            <entry.View
              view={view}
              self={self}
              disabled={disabled}
              onMove={(move) => session.move(move)}
            />
          ) : (
            <p className="px-4 text-center text-sm text-white/60">{state.statusText}</p>
          )}
        </div>

        {showResult && !isSpectator ? (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-[10px] bg-black/20 px-3 py-2">
            <span className="text-sm">{resultText}</span>
            <button
              type="button"
              className="min-h-10 shrink-0 rounded-lg border border-accent/60 bg-accent/15 px-3 py-1.5 text-sm text-accent hover:bg-accent/25"
              onClick={() => session.requestRestart()}
            >
              再来一局
            </button>
          </div>
        ) : null}
        {showResult && isSpectator ? (
          <div className="shrink-0 rounded-[10px] bg-black/20 px-3 py-2 text-sm">
            {resultText}
          </div>
        ) : null}
      </div>

      <ChatDock
        messages={messages}
        unread={unread}
        disabled={state.status !== 'connected' || state.peerAway}
        taunts={entry?.chatTaunts}
        onSend={session.sendChat}
        onMarkRead={session.markChatRead}
      />
    </div>
  )
}
